import React, { useEffect, useState } from "react";
import { Button, Grid, Paper, Stack, Typography, IconButton, Tooltip, Alert } from "@mui/material";
import { Refresh as RefreshIcon, GetApp as ExportIcon } from "@mui/icons-material";

import CustomerMasterTable from "../../../components/admin/customer-master/CustomerMasterTable";
import CustomerMasterDetailsDialog from "../../../components/admin/customer-master/CustomerMasterDetailsDialog";
import { SearchRecords } from "../../../components/Common/SearchRecords";
import {
    useGetAdminCustomerMasterSearchQuery,
    useGetAdminCustomerMasterDetailsQuery,
    useGetAdminCustomerMasterStatsQuery,
    useLazyGetCustomerDeletionImpactQuery,
    useDeleteCustomerMasterMutation,
} from "../../../store/customerMaster/customerMasterApis";

const CustomerMasterContainer = (props) => {
    const {
        pagination,
        selectedCustomerMaster,
        showCustomerMasterDetails,
        handlePaginationState,
        setSelectedCustomerMaster,
        setShowCustomerMasterDetails,
    } = props;

    const [selectedCustomerMasterForDetails, setSelectedCustomerMasterForDetails] = useState(null);

    // Build query parameters
    const queryParams = {
        pageNo: pagination.pageNo,
        limit: pagination.limit,
        sort_by: pagination.sortField,
        sort_order: pagination.sortBy,
        searchTerm: pagination.keywords || "all", // Use "all" if no search term
    };

    // API queries
    const {
        data: customerMasterData,
        isLoading: customerMasterLoading,
        error: customerMasterError,
        refetch: refetchCustomerMaster,
    } = useGetAdminCustomerMasterSearchQuery(queryParams);

    const {
        data: customerMasterDetailsData,
        isLoading: customerMasterDetailsLoading,
        error: customerMasterDetailsError,
    } = useGetAdminCustomerMasterDetailsQuery(selectedCustomerMaster?.id, {
        skip: !selectedCustomerMaster?.id,
    });

    const {
        data: statsData,
        refetch: refetchCustomerMasterStats,
    } = useGetAdminCustomerMasterStatsQuery();
    const [triggerCustomerDeletionImpact] = useLazyGetCustomerDeletionImpactQuery();
    const [deleteCustomerMaster, { isLoading: deletingCustomer }] = useDeleteCustomerMasterMutation();

    // Handle customer master details
    useEffect(() => {
        if (customerMasterDetailsData) {
            setSelectedCustomerMasterForDetails(customerMasterDetailsData);
        }
    }, [customerMasterDetailsData]);

    const handleViewDetails = (customerMaster) => {
        setSelectedCustomerMaster(customerMaster);
        setShowCustomerMasterDetails(true);
    };

    const handleCloseDetails = () => {
        setShowCustomerMasterDetails(false);
        setSelectedCustomerMaster(null);
        setSelectedCustomerMasterForDetails(null);
    };

    const handlePagination = (setting) => {
        handlePaginationState(setting);
    };

    const handleFetchDeletionImpact = async (customerId) => {
        if (!customerId) {
            throw new Error("Customer ID is missing");
        }

        const impact = await triggerCustomerDeletionImpact(customerId).unwrap();
        return impact;
    };

    const handleDeleteCustomer = async (customerId, deletionReason = null) => {
        if (!customerId) {
            throw new Error("Customer ID is missing");
        }

        await deleteCustomerMaster({
            id: customerId,
            deletion_reason: deletionReason,
        }).unwrap();

        await Promise.all([refetchCustomerMaster(), refetchCustomerMasterStats()]);
        handleCloseDetails();
        alert("Customer and related records deleted successfully. Backup snapshot stored.");
    };

    return (
        <Stack spacing={2}>
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sx={{ alignSelf: "center" }}>
                    <Typography variant="h6">
                        Customer Master
                        <span style={{ color: "gray", fontSize: "10px", verticalAlign: "super", marginLeft: 8 }}>
                            ({customerMasterData?.count || 0})
                        </span>
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={6}
                    sx={{ alignSelf: "center" }}
                    display={"flex"}
                    flexDirection={"row"}
                    justifyContent={"flex-end"}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SearchRecords handlePagination={handlePagination} pagination={pagination} />
                        <Tooltip title="Refresh">
                            <IconButton size="small" onClick={refetchCustomerMaster}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export">
                            <IconButton size="small">
                                <ExportIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Grid>
            </Grid>

            {/* Error Alert */}
            {customerMasterError && (
                <Alert severity="error">
                    {customerMasterError?.data?.message || "Failed to fetch customer master data"}
                </Alert>
            )}

            {/* Search Instructions */}
            {!pagination.keywords || pagination.keywords.length < 3 ? (
                <Alert severity="info">
                    {pagination.keywords
                        ? "Enter at least 3 characters to search customers by name, mobile number, or email."
                        : "Showing all customers. Enter at least 3 characters in the search box to filter results."}
                </Alert>
            ) : null}

            {/* Statistics Cards */}
            {statsData && (
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="h4" color="primary">
                                {statsData.total_customers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Customers
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="h4" color="success.main">
                                {statsData.staff_created_customers}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Staff Created
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="h4" color="warning.main">
                                {statsData.total_orders}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Orders
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="h4" color="info.main">
                                {statsData.total_warranty_registrations + statsData.total_insurance_renewals}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Registrations
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* Customer Table */}
            <Grid container>
                <Grid item xs={12}>
                    <CustomerMasterTable
                        data={customerMasterData?.results || []}
                        loading={customerMasterLoading}
                        onViewDetails={handleViewDetails}
                        pagination={pagination}
                        onPaginationChange={handlePagination}
                    />
                </Grid>
            </Grid>

            {/* Customer Details Dialog */}
            <CustomerMasterDetailsDialog
                open={showCustomerMasterDetails}
                onClose={handleCloseDetails}
                customerMasterData={selectedCustomerMasterForDetails}
                loading={customerMasterDetailsLoading}
                error={customerMasterDetailsError?.data?.message}
                onFetchDeletionImpact={handleFetchDeletionImpact}
                onDeleteCustomer={handleDeleteCustomer}
                deletingCustomer={deletingCustomer}
            />
        </Stack>
    );
};

export default CustomerMasterContainer;
